import { registerSpecimen } from "../slices/specimenSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetches a Specimen resource by ID:
 * - Checks the Redux store first
 * - If not present, fetches from FHIR server and registers it
 * - Logs an error AuditEvent if fetch fails
 *
 * @param {string} specimenId
 * @param {object} fhirClient
 * @param {object} store - Redux store instance
 * @param {object} options
 * @param {boolean} options.forceFetch
 * @returns {object|null} Specimen resource or null if not found
 */
export const fetchSpecimen = async (
  specimenId,
  fhirClient,
  store,
  { forceFetch = false } = {}
) => {
  if (!specimenId || !fhirClient) return null;

  const cached = store.getState().specimens?.[specimenId];
  if (cached && !forceFetch) {
    return cached;
  }

  try {
    const fetched = await fhirClient.request(`Specimen/${specimenId}`);
    store.dispatch(registerSpecimen(fetched));
    return fetched;
  } catch (err) {
    console.error("❌ Failed to fetch Specimen:", err);
    const status = err?.response?.status || err?.status || 500;

    logErrorEvent({
      errorType: "read",
      error: {
        message: err?.message || "Specimen fetch failed",
        status,
        resourceId: `Specimen/${specimenId}`,
      },
      description: `Failed to retrieve Specimen/${specimenId} from FHIR server`,
      store,
    });

    return { error: status };
  }
};

/**
 * Fetches a Specimen resource by reference string (e.g., "Specimen/abc123")
 * - Validates format
 * - Extracts ID and delegates to fetchSpecimen
 *
 * @param {string} ref
 * @param {object} fhirClient
 * @param {object} store - Redux store instance
 * @param {object} options
 * @returns {object|null} Specimen resource or null if not found
 */
export const fetchSpecimenByReference = async (
  ref,
  fhirClient,
  store,
  options = {}
) => {
  if (!ref || !ref.startsWith("Specimen/") || ref === "Specimen/undefined") {
    console.warn("⚠️ Invalid Specimen reference:", ref);
    return null;
  }

  const specimenId = ref.split("/")[1];
  if (!specimenId) {
    console.warn("⚠️ Could not extract specimen ID from reference:", ref);
    return null;
  }

  return fetchSpecimen(specimenId, fhirClient, store, options);
};
